import cn from 'classnames'
import { usePartnerId } from 'legacy/lut/classes/helpers/partner360/usePartnerId'
import { selectFinancialYear } from 'legacy/lut/features/partner360/configuration'
import { useGetPartnerData } from 'legacy/lut/features/partner/hooks'
import { FC, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations'
import { useAppDispatch } from 'cng-common/hooks/useAppDispatch'
import { Button } from 'core-components/library/button'
import { Switch } from 'core-components/library/checkbox'
import { Checkbox } from 'core-components/library/checkbox'
import { Loader } from 'core-components/library/loader'
import { setPublishingMode } from 'core/store/appSlice'
import { selectPublishingMode } from 'core/store/selectors/selectPublishingMode'

import { applyPlanningWizardFlags } from '../../../../api/applyPlanningWizardFlags'
import { IS_SHOW_HIDE_WIZARD_CHECKBOX, LOADER_DIMENSION } from '../../../../config'
import { useWizardSave } from '../../../../context/WizardSaveContext'
import {
  selectIsHideWizard,
  selectWizardIsLoading,
  setIsHideWizard,
  setWizardIsLoading,
} from '../../../../features/planningWizard'
import { IWizardStep } from '../../../../models/IWizardStep'
import { TRANSLATION_KEYS } from '../../translations'

import { HpIcon } from './icons/HpIcon'

import styles from './WizardHeader.module.scss'

interface IWizardHeaderProps {
  onClose: () => void
  widgetParams: IObjectAny
  pid: string
  activeStep: IWizardStep | null
}

export const WizardHeader: FC<IWizardHeaderProps> = ({
  onClose,
  widgetParams,
  pid,
  activeStep,
}) => {
  /** ------------------------- Selectors ------------------------- */

  const fYear = useSelector(selectFinancialYear)
  const isHideWizard = useSelector(selectIsHideWizard)
  const isEditMode = useSelector(selectPublishingMode)
  const isLoading = useSelector(selectWizardIsLoading)

  /** ------------------------- Hooks ------------------------- */

  const dispatch = useAppDispatch()
  const wizardSave = useWizardSave()
  const partnerId = usePartnerId(widgetParams, pid)
  const { partnerData } = useGetPartnerData(pid)

  /** ------------------------- Variables ------------------------- */

  const partnerName = useMemo(
    () => partnerData?.communicationName,
    [partnerData?.communicationName]
  )
  const isShowSaveAndDiscard = useMemo(() => wizardSave && isEditMode, [isEditMode, wizardSave])
  const curFYear = useMemo(() => fYear % 100, [fYear])
  const replaceParams = useMemo(
    () => [
      { key: '{PartnerName}', value: partnerName },
      { key: '{CurFYear}', value: curFYear.toString() },
      { key: '{PartnerID}', value: partnerId },
    ],
    [curFYear, partnerId, partnerName]
  )

  /** ------------------------- User actions handlers ------------------------- */

  const handleVisibilityFlagChange = useCallback(
    async (newIsHideWizard: boolean) => {
      dispatch(setWizardIsLoading(true))

      await applyPlanningWizardFlags({ partnerId, isHideWizard: newIsHideWizard })
      dispatch(setIsHideWizard(newIsHideWizard))

      dispatch(setWizardIsLoading(false))
    },
    [dispatch, partnerId]
  )

  const handleEditModeToggle = useCallback(() => {
    dispatch(setPublishingMode(!isEditMode))
  }, [dispatch, isEditMode])

  const handleSave = useCallback(async () => {
    if (wizardSave) {
      await wizardSave.save()
    }
  }, [wizardSave])

  const handleDiscard = useCallback(() => {
    if (isEditMode) {
      dispatch(setPublishingMode(false))
    }
  }, [dispatch, isEditMode])

  /** ------------------------- Render ------------------------- */

  return (
    <div className={styles.header}>
      <div className={styles.wrapper}>
        <HpIcon />
        <div className={styles.info}>
          <div className={styles.title}>
            <Translation text={TRANSLATION_KEYS.wizardTitle} replaceParams={replaceParams} />
          </div>
          <div className={styles.period}>
            <Translation text={TRANSLATION_KEYS.wizardSubTitle} replaceParams={replaceParams} />
          </div>
        </div>
      </div>
      <div className={styles.buttonsWrapper}>
        {isLoading && <Loader className={styles.loader} dimension={LOADER_DIMENSION} />}
        {IS_SHOW_HIDE_WIZARD_CHECKBOX && (
          <Checkbox
            label={<Translation text={TRANSLATION_KEYS.isHideWizard} />}
            checked={isHideWizard}
            onChange={handleVisibilityFlagChange}
            className={styles.checkbox}
            disabled={isLoading}
          />
        )}
        {!activeStep?.isReadOnly && (
          <Switch
            padding
            className={cn(styles.btn)}
            label={
              <div className={styles.editButton}>
                <Translation text={TRANSLATION_KEYS.editButton} span />
              </div>
            }
            checked={isEditMode}
            key="publishingMode"
            onChange={handleEditModeToggle}
          />
        )}
        {isShowSaveAndDiscard && (
          <>
            <Button onClick={handleSave} disabled={wizardSave?.isSaveDisabled}>
              <Translation text={TRANSLATION_KEYS.saveButton} />
            </Button>
            <Button onClick={handleDiscard} disabled={wizardSave?.isSaveDisabled}>
              <Translation text={TRANSLATION_KEYS.discardButton} />
            </Button>
          </>
        )}
        <Button onClick={onClose}>
          <div className={styles.closeButton}>
            <Translation text={TRANSLATION_KEYS.closeButton} span />
            <i className={'kbase kbase-close'} />
          </div>
        </Button>
      </div>
    </div>
  )
}
